import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Pressable } from 'react-native';
import {
  DNABox,
  DNAButton,
  DNACard,
  DNAText,
  Iffy,
  DNAChip,
  DNAIcon,
  Hoverable,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  DNACustomFieldChipList,
  DNADocumentThumbnail,
  DNAEmpty,
  EmptyVariant,
  TitleBar,
} from 'src/components/DNA';
import { CustomDeckORM, DocumentVersionORM, FolderItemORM } from 'src/types/types';
import { isCustomDeckORM } from 'src/types/typeguards';
import { useDispatch } from 'src/state/redux';
import folderQuery from 'src/state/redux/folder/query';
import {
  DocumentVersionUpdateInfo,
  DocumentVersionUpdateInfoPath,
  useAllFoldersUpdatedDocsMap,
} from 'src/state/redux/selector/folder';
import { folderActions } from 'src/state/redux/slice/folder';
import { customDeckActions } from 'src/state/redux/slice/customDeck';
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal';
import { useAppSettings } from 'src/state/context/AppSettings';
import { upgradeFolderDocuments } from 'src/state/redux/saga';
import { DNAMyUpdatesButtonActions, FolderItemStatus } from './DNAMyUpdates/DNAMyUpdatesButtonActions';
import UpdatedItemListRow from './DNAMyUpdates/UpdatedItemListRow';
import { STATUS_MESSAGES, getDocumentStatusType, getBadgeText } from './utils/badgeUtils';

const S = StyleSheet.create({
  documentListHeader: {
    height: 48,
    padding: 14,
    backgroundColor: colors['color-gray-10'],
  },
  documentContainerCard: {
    padding: 24,
  },
  documentContainerCardUI2: {
    padding: 16,
  },
  lastRow: {
    borderRadius: 0,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  thumbnailContainer: {
    borderWidth: 1,
    borderColor: colors['color-gray-80'],
  },
  updatesContainer: {
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: colors['color-gray-100'],
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: colors['color-text-white'],
  },
  updatesContainerUI2: {
    borderWidth: 0,
    borderTopWidth: 0,
  },
  documentCardRow: {
    borderWidth: 1,
    borderColor: colors['color-gray-80'],
  },
  documentCardRowHover: {
    backgroundColor: colors['color-ui2-neutral-50'],
  },
})

interface DocumentInfoProps {
  documentVersionORM: DocumentVersionORM,
  isExpanded: boolean,
}
export const DocumentInfo: React.FC<DocumentInfoProps> = ({ documentVersionORM, isExpanded }) => {
  const dispatch = useDispatch();

  const handleOpenContentPreview = () => {
    dispatch(
      contentPreviewModalActions.setModalVisibility({
        documentVersionId: documentVersionORM.model.id,
        content: documentVersionORM,
        isOpen: true,
        isFullWindow: false,
      }),
    );
  };

  return (
    <DNABox appearance="col" fill>
      <DNABox appearance="row" childFill={1} spacing="md">
        <Pressable onPress={handleOpenContentPreview}>
          <DNABox style={S.thumbnailContainer} alignY="center">
            <DNADocumentThumbnail.Hover
              documentVersionORM={documentVersionORM}
              height={80}
              width={144}
            />
          </DNABox>
        </Pressable>
        <DNABox fill appearance="col" spacing="sm">
          <DNABox spacing="sm" alignY="center" childFill={1}>
            <DNACustomFieldChipList documentVersionORM={documentVersionORM} />
            <DNAText
              numberOfLines={2}
              style={{ textDecorationLine: 'underline' }}
              h5
              onPress={handleOpenContentPreview}
            >{documentVersionORM.model.title}
            </DNAText>
          </DNABox>
          <DNAText
            numberOfLines={isExpanded ? 10 : 2}
          >{documentVersionORM.model.releaseNotes}
          </DNAText>

        </DNABox>
      </DNABox>
    </DNABox>
  )
}

export const DocumentUnavailable = () => {
  return (
    <DNABox appearance="col" fill spacing="sm">
      <DNAText
        status="tertiary"
        bold
        h5
      >
        This source file is no longer available.
      </DNAText>
      <DNABox
        alignX="center"
        alignY="center"
        style={{
          width: 144,
          height: 80,
          borderWidth: 1,
          borderColor: colors['color-ui2-neutral-200'],
          backgroundColor: colors['color-ui2-neutral-50'],
        }}

      >
        <DNAIcon.Styled
          appearance="outline"
          status="subtle"
          size="xl"
          name="file-x-regular"
        />
      </DNABox>

    </DNABox>
  )
}

interface UpdatedDocumentsListProps {
  folderItems: DocumentVersionUpdateInfo[],
  setFolderItems: Dispatch<SetStateAction<DocumentVersionUpdateInfo[]>>,
  documentVersionORM?: DocumentVersionORM,
  isExpanded: boolean,
}
const UpdatedFolderItemsList: React.FC<UpdatedDocumentsListProps> = ({
  folderItems,
  setFolderItems,
  documentVersionORM,
  isExpanded,
}) => {
  return (
    <DNABox
      appearance="col"
      spacing="sm"
      fill
      style={{
        zIndex: 2,
        paddingLeft: 16,
        paddingTop: isExpanded ? 24 : 0,
      }}
    >
      <Iffy is={isExpanded}>
        <DNABox appearance="col" spacing="lg">
          {
            folderItems.map((item, idx) => (
              <UpdatedItemListRow
                key={`${item.folderItem.model.id}_${idx}`}
                updateInfo={item}
                setFolderItems={setFolderItems}
                documentVersionORM={documentVersionORM}
              />
            ))
          }
        </DNABox>
      </Iffy>
    </DNABox>
  )
}

interface DocumentUpdatedCardProps {
  documentVersionORM?: DocumentVersionORM,
  isLast?: boolean,
  updates: DocumentVersionUpdateInfo[],
  allExpanded: boolean,
}
const DocumentUpdatedCard: React.FC<DocumentUpdatedCardProps> = ({
  documentVersionORM,
  isLast,
  updates,
  allExpanded,
}) => {
  const currentDocumentStatus = getDocumentStatusType(documentVersionORM, updates)

  const changeType = documentVersionORM?.model.changeType?.toLowerCase()

  const renderBadges = () => {
    const currentStatus = STATUS_MESSAGES[currentDocumentStatus];
    return (
      <DNABox appearance="row" spacing="xs">
        {currentStatus.badges.map((badge) => {
          const badgeText = getBadgeText(badge, changeType || '');

          return (
            <DNAChip
              key={`${currentDocumentStatus}-${badge.appearance}-${badge.status}`}
              appearance={badge.appearance}
              status={badge.status}
            >
              {badgeText}
            </DNAChip>
          );
        })}
        <DNAChip
          appearance="outline"
          status="secondary"
          size="md"
          rounded
        >
          {`${updates.length} ITEM(S) IMPACTED`}
        </DNAChip>
      </DNABox>
    );
  };

  const [folderItems, setFolderItems] = useState(updates)
  const [isExpanded, setIsExpanded] = useState(allExpanded)
  const [isHovered, setIsHovered] = useState(false)
  const { deviceMode } = useAppSettings()

  const toggleHover = () => {
    setIsHovered(currVal => !currVal)
  }
  const hasManyInStatus = (items: DocumentVersionUpdateInfo[], status: FolderItemStatus) =>
    items.filter(upd => upd.status === status).length > 1

  const [showAcknowledgeAll, setShowAcknowledgeAll] = useState(
    hasManyInStatus(updates, FolderItemStatus.AutoUpdated))
  const [showUpdateAll, setShowUpdateAll] = useState(
    hasManyInStatus(updates, FolderItemStatus.Outdated))
  const dispatch = useDispatch()

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  useEffect(() => {
    setIsExpanded(allExpanded)
  }, [allExpanded])

  useEffect(() => {
    setFolderItems(updates)
  }, [updates])

  useEffect(() => {
    // Check current status in list and update batch buttons
    setShowAcknowledgeAll(hasManyInStatus(folderItems, FolderItemStatus.AutoUpdated))
    setShowUpdateAll(hasManyInStatus(folderItems, FolderItemStatus.Outdated))
  }, [folderItems])

  const handleBatchAction = (action: 'acknowledge' | 'update') => {
    const itemsToUpdate: DocumentVersionUpdateInfo[] = []

    // Get array with affected items
    folderItems.forEach(item => {
      const isAcknowledge = action === 'acknowledge' && item.status === FolderItemStatus.AutoUpdated
      const isUpdate = action === 'update' && item.status === FolderItemStatus.Outdated
      if ((isAcknowledge || isUpdate)) {
        itemsToUpdate.push(item)
      }
    })

    // We only support batch actions for Acknowledge and Update
    const targetStatus = action === 'acknowledge'
      ? FolderItemStatus.AutoUpdated
      : FolderItemStatus.Outdated

    // Execute selected action on affected items
    DNAMyUpdatesButtonActions[targetStatus].action?.(dispatch, itemsToUpdate)

    // Hide the batch action button
    if (action === 'acknowledge') {
      setShowAcknowledgeAll(false)
    }
    else {
      setShowUpdateAll(false)
    }

    setFolderItems(folderItems)
  }

  return (

    <DNACard
      appearance="flat"
      style={[
        S.documentContainerCardUI2,
        S.documentCardRow,
        isLast && S.lastRow,
        !isExpanded && isHovered && S.documentCardRowHover,
      ]}
    >
      <Hoverable onHoverIn={toggleHover} onHoverOut={toggleHover}>
        <Pressable onPress={toggleExpanded}>
          <DNABox appearance="col" fill style={!isExpanded && isHovered && S.documentCardRowHover}>
            <DNABox appearance="col" spacing="sm">
              <DNABox appearance="row" spacing="between">
                {renderBadges()}
                <DNABox appearance="row" spacing="sm" alignY="center">
                  <Iffy is={showAcknowledgeAll || showUpdateAll}>
                    <DNAButton
                      appearance="outline"
                      status="tertiary"
                      size={deviceMode === 'desktop' ? 'md' : 'xl'}
                      onPress={() => handleBatchAction(showAcknowledgeAll ? 'acknowledge' : 'update')}
                    >
                      {showAcknowledgeAll ? 'Acknowledge all' : 'Update all'}
                    </DNAButton>
                  </Iffy>
                  <DNAButton
                    appearance="outline"
                    size={deviceMode === 'desktop' ? 'md' : 'xl'}
                    status="tertiary"
                    onPress={toggleExpanded}
                    iconLeft={isExpanded ? 'caret-up' : 'caret-down'}
                  />
                </DNABox>
              </DNABox>
              <Iffy is={documentVersionORM}>
                <DNABox>
                  <DocumentInfo documentVersionORM={documentVersionORM!} isExpanded={allExpanded} />
                </DNABox>
              </Iffy>
              <Iffy is={!documentVersionORM}>
                <DocumentUnavailable />
              </Iffy>
              <DNABox>
                {/* Simple vertical line connecting source file to folder list */}
                <Iffy is={isExpanded}>
                  <DNABox
                    style={{
                      left: 71,
                      top: -9,
                      bottom: 10,
                      width: 2,
                      backgroundColor: colors['color-ui2-neutral-200'],
                      zIndex: 1,
                    }}
                  />
                </Iffy>
                <UpdatedFolderItemsList
                  folderItems={folderItems}
                  setFolderItems={setFolderItems}
                  documentVersionORM={documentVersionORM}
                  isExpanded={isExpanded}
                />
              </DNABox>
            </DNABox>
          </DNABox>
        </Pressable>
      </Hoverable>
    </DNACard>
  )
}

const getPathString = (items: DocumentVersionUpdateInfoPath[]) =>
  items.map(x => x.title).join(' / ')

interface DocumentsUpdatesProps {
  isTablet?: boolean,
}
const DocumentsUpdates: React.FC<DocumentsUpdatesProps> = ({
  isTablet,
}) => {
  const currentActiveFilter = useMemo(() => folderQuery.merge(folderQuery.filters.active), [])
  const updatedDocumentsMap = useAllFoldersUpdatedDocsMap(currentActiveFilter)
  const [documents, setDocuments] = useState<[DocumentVersionORM, DocumentVersionUpdateInfo[]][]>([])
  const [allExpanded, setAllExpanded] = useState(false)
  const { deviceMode } = useAppSettings()
  const dispatch = useDispatch()
  const [showGlobalAction, setShowGlobalAction] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const toggleAllExpanded = () => {
    setAllExpanded(!allExpanded)
  }

  useEffect(() => {
    // Order from most recent to older
    const array = Array.from(updatedDocumentsMap)
    array.sort((a, b) => a[0] && b[0]
      ? (a[0].model.updatedAt < b[0].model.updatedAt ? 1 : -1)
      : (a[0] ? 1 : (b[0] ? -1 : 0)))

    // Order folders
    array.forEach(entry => {
      entry[1].sort((a, b) => (getPathString(a.path) > getPathString(b.path)) ? 1 : -1)
    })

    setDocuments(array)
  }, [updatedDocumentsMap])

  useEffect(() => {
    if (isProcessing) return

    const hasPending = documents.some(item =>
      item[1].some(upd =>
        upd.status === FolderItemStatus.AutoUpdated ||
        upd.status === FolderItemStatus.Outdated))
    setShowGlobalAction(hasPending)
  }, [documents, isProcessing])

  const handleGlobalAcknowledge = () => {
    const itemsToAcknowledge: DocumentVersionUpdateInfo[] = []
    const itemsToUpdate: DocumentVersionUpdateInfo[] = []

    documents.forEach(([_, infos]) => {
      infos.forEach(info => {
        if (info.status === FolderItemStatus.AutoUpdated) {
          itemsToAcknowledge.push(info)
        } else if (info.status === FolderItemStatus.Outdated) {
          itemsToUpdate.push(info)
        }
      })
    })

    if (itemsToAcknowledge.length) {
      const folderItemORMs: FolderItemORM[] = []
      const customDeckORMs: CustomDeckORM[] = []
      itemsToAcknowledge.forEach(obj => {
        if (isCustomDeckORM(obj.folderItem.relations.itemORM)) {
          customDeckORMs.push(obj.folderItem.relations.itemORM)
        } else {
          folderItemORMs.push(obj.folderItem)
        }
      })
      dispatch(folderActions.acknowledgeAutoUpdate(folderItemORMs))
      dispatch(customDeckActions.acknowledgeAutoUpdate(customDeckORMs))
    }
    if (itemsToUpdate.length) {
      const folderItemIds: string[] = []
      const customDeckORMs: CustomDeckORM[] = []
      itemsToUpdate.forEach(obj => {
        if (isCustomDeckORM(obj.folderItem.relations.itemORM)) {
          customDeckORMs.push(obj.folderItem.relations.itemORM)
        } else {
          folderItemIds.push(obj.folderItem.model.id)
        }
      })
      upgradeFolderDocuments(dispatch, {
        folderItemId: folderItemIds,
        skipGracePeriod: true,
      })
      dispatch(customDeckActions.upgradeCustomDecks({ customDeckORMs }))
    }

    const newDocuments: [DocumentVersionORM, DocumentVersionUpdateInfo[]][] = documents.map(([doc, infos]) => [
      doc,
      infos.map(info => {
        if (itemsToAcknowledge.includes(info)) {
          return { ...info, status: FolderItemStatus.Acknowledged }
        }
        if (itemsToUpdate.includes(info)) {
          return { ...info, status: FolderItemStatus.Updated }
        }
        return info
      }),
    ])

    if (itemsToAcknowledge.length || itemsToUpdate.length) {
      setDocuments(newDocuments)
    }
  }

  const totalUpdatesCount = Array.from(updatedDocumentsMap.values()).flat().length;
  const isEmpty = documents.length === 0

  return (
    <DNABox appearance="col" spacing="md" style={!isTablet && S.updatesContainerUI2}>
      <TitleBar
        title="My Updates"
        description="Review and update your content"
        badge={
          (documents.length > 0)
            ? (
              <DNAChip appearance="filled" status="danger" size="md" rounded>
                {totalUpdatesCount.toString()}
              </DNAChip>
            )
            : undefined
        }
      >
        <TitleBar.Button>
          <Iffy is={!isEmpty}>
            <DNABox appearance="row" spacing="sm">
              <Iffy is={showGlobalAction}>
                <DNAButton
                  size={deviceMode === 'desktop' ? 'md' : 'xl'}
                  status="primary"
                  appearance="outline"
                  disabled={isProcessing}
                  onPress={async () => {
                    setIsProcessing(true)
                    try {
                      handleGlobalAcknowledge()
                    }
                    finally {
                      setIsProcessing(false)
                    }
                  }}
                >
                  Acknowledge all
                </DNAButton>
              </Iffy>
              <DNAButton
                size={deviceMode === 'desktop' ? 'md' : 'xl'}
                status="primary"
                appearance="outline"
                onPress={toggleAllExpanded}
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </DNAButton>
            </DNABox>
          </Iffy>
        </TitleBar.Button>
      </TitleBar>

      <Iffy is={isEmpty}>
        <DNAEmpty emptyVariant={EmptyVariant.MyUpdatesEmpty} />
      </Iffy>

      <Iffy is={!isEmpty}>
        {documents.map((item, idx) => {
          const docId = item[0]?.model?.id ?? '-1'
          return (
            <DocumentUpdatedCard
              isLast={idx === documents.length - 1}
              key={docId}
              documentVersionORM={item[0]}
              updates={item[1]}
              allExpanded={allExpanded}
            />
          )
        })}
      </Iffy>
    </DNABox>
  )
}

export default DocumentsUpdates
